import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { getNewApiClient } from "./client";
import { AuthenticationService } from "./authentication";
import {
  GetMyRunsDocument,
  GetMyRunsQuery,
  User,
  WhoAmIDocument,
  WhoAmIQuery,
} from "./__generated__/backend_gateway-types";
import { Run } from "../appsync/types";
import { decorateRuns } from "../appsync/service";

export class ApiService {
  private static instance: ApiService;
  private client: ApolloClient<NormalizedCacheObject>;
  private user: User | null;

  private constructor(client: ApolloClient<NormalizedCacheObject>) {
    this.client = client;
    this.user = null;
  }

  public static getInstance(reset: boolean = false): ApiService {
    if (!ApiService.instance || reset) {
      if (ApiService.instance) {
        ApiService.instance.client.stop();
      }
      ApiService.instance = new ApiService(getNewApiClient());

      // Set up token refresh callback to recreate client when token changes
      AuthenticationService.setOnTokenRefresh(() => {
        console.info("Token refreshed, recreating ApiService client");
        ApiService.instance.reconnect();
      });
    }
    return ApiService.instance;
  }

  getClient(): ApolloClient<NormalizedCacheObject> {
    return this.client;
  }

  reset(): void {
    this.client.resetStore();
    this.reconnect();
  }

  reconnect(): void {
    this.client.stop();
    this.client = ApiService.getInstance(true).getClient();
  }

  stop(): void {
    this.client.resetStore();
    this.client.stop();
  }

  async setWhoAmI(): Promise<void> {
    const result = await this.client.query({
      query: WhoAmIDocument,
    });
    const { error } = result;
    if (error) {
      console.error(error);
      return;
    }
    const { data }: { data: WhoAmIQuery } = result;
    if (data) {
      this.user = data.whoAmI || null;
    }
  }

  async whoAmI(): Promise<User | null> {
    if (this.user === null) {
      await this.setWhoAmI();
    }
    return this.user;
  }

  async getMyRunsSnapshot(selectedCustomerId: string): Promise<Run[]> {
    const result = await this.client.query<GetMyRunsQuery>({
      query: GetMyRunsDocument,
      fetchPolicy: "network-only",
      context: { atomicCustomerId: selectedCustomerId },
    });
    const runs = result.data?.getMyRuns || [];
    return decorateRuns(
      runs.map((run) => ({
        id: run.id,
        displayId: null,
        runSettings: {
          inputId: run.runSettings.inputId,
          organizationId: run.runSettings.organizationId,
          workflowId: run.runSettings.workflowId,
          settingsNameAndVersion: run.runSettings.settingsNameAndVersion,
          runAttemptId: run.runSettings.runAttemptId,
        },
        status: run.status,
        createdAt: run.createdAt,
        updatedAt: run.updatedAt,
        stepIds: run.stepIds || "[]",
        initiatedBy: run.initiatedBy ?? null,
        name: run.name ?? null,
        description: run.description ?? null,
        atStep: run.atStep ?? null,
        totalSteps: run.totalSteps ?? null,
        engineInProgress: run.engineInProgress ?? null,
      })),
    );
  }
}
